/**
 * Paginación del detalle de tareas (cotización tipo `tareas`).
 *
 * El template no usa saltos de página de CSS: cada `.cot-page` es un bloque de
 * alto fijo (1122px = A4 a 96dpi) con el pie posicionado al fondo, así que el
 * reparto de secciones en hojas lo decide este módulo antes de renderizar. Si
 * una hoja se pasa, `overflow: visible` la deja crecer y el contenido se pinta
 * encima del pie — silencioso, no falla nada.
 *
 * Por eso el reparto estima el alto real en píxeles en vez de contar secciones:
 * el criterio anterior ("una tabla pesa 2, la hoja aguanta 4") metía una tabla
 * de 14 filas y una lista de 13 ítems en la misma hoja (1339px de contenido
 * contra 971px de hueco).
 *
 * Las constantes salen de medir el preview a ancho A4 (794px) — ver
 * `estimateSectionHeight`. El estimador queda del lado conservador: sobre 35
 * secciones reales el peor caso se quedó 9px corto y la media sobra ~5%.
 *
 * MANTENER EN SINCRONÍA con `api/src/lib/pdf-layout.ts` (el PDF de Gotenberg usa
 * la copia del backend; si divergen, el PDF y la vista previa parten distinto).
 */

export interface ContentSection {
  heading: string;
  html: string;
}

// ─── Geometría de la hoja (px, A4 a 96dpi) ───

const PAGE_H = 1122;
const PAGE_HEADER_H = 24;          // franja de color superior
const BODY_PAD_X = 40;             // .cot-body padding lateral
const CONTENT_W = 794 - BODY_PAD_X * 2;

// Todo lo que ocupa la hoja además del contenido: padding del body (28+50) más
// la cabecera de la tarea (nav + título + monto) en la primera, o la línea de
// "(cont.)" en las siguientes. Medido: 250-274 y 127-129 respectivamente.
const FIRST_PAGE_CHROME = 276;
const CONT_PAGE_CHROME = 129;

// Colchón para el error del estimador.
const SAFETY = 15;

const FIRST_PAGE_BUDGET = PAGE_H - PAGE_HEADER_H - FIRST_PAGE_CHROME - SAFETY;  // 807
const CONT_PAGE_BUDGET = PAGE_H - PAGE_HEADER_H - CONT_PAGE_CHROME - SAFETY;    // 954

// ─── Métricas de texto ───

const BODY_FS = 13.33;      // 10pt
const TABLE_FS = 12;        // 9pt
const LINE_H = 1.7;
const CHAR_RATIO = 0.5;     // ancho medio de glifo / font-size, Inter

const HEADING_H = 27, HEADING_MT = 16, HEADING_MB = 8;
const P_MARGIN = 6;
const LIST_MARGIN = 8, LIST_PAD = 20, LI_MB = 4;
const TABLE_MARGIN = 12;
const CELL_CHROME = 13;     // padding vertical + borde de fila
const CELL_BLOCK_M = 12;    // margen del <p> que el editor mete dentro de cada celda

const BLOCK_TAGS = '(h[1-6]|p|ul|ol|table|blockquote|pre|div)';

interface Block {
  tag: string;
  html: string;
}

function stripTags(html: string): string {
  return html
    .replace(/<[^>]+>/g, ' ')
    .replace(/&nbsp;/g, ' ')
    .replace(/&amp;/g, '&')
    .replace(/&[a-z]+;/gi, 'x')
    .replace(/\s+/g, ' ')
    .trim();
}

function textHeight(text: string, width: number, fontSize = BODY_FS): number {
  const lines = Math.max(1, Math.ceil((text.length * fontSize * CHAR_RATIO) / width));
  return lines * fontSize * LINE_H;
}

/** Bloques de primer nivel, respetando el anidamiento de la misma etiqueta. */
function splitTopLevel(html: string): Block[] {
  const blocks: Block[] = [];
  const next = new RegExp('<' + BLOCK_TAGS + '[\\s>]', 'gi');
  let cursor = 0;
  while (cursor < html.length) {
    next.lastIndex = cursor;
    const match = next.exec(html);
    if (!match) break;
    const tag = match[1].toLowerCase();
    const start = match.index;
    const open = new RegExp('<' + tag + '[\\s>]', 'gi');
    const close = new RegExp('</' + tag + '\\s*>', 'gi');
    let depth = 0, pos = start, end = html.length;
    while (pos < html.length) {
      open.lastIndex = pos;
      close.lastIndex = pos;
      const o = open.exec(html);
      const c = close.exec(html);
      if (!c) break;
      if (o && o.index < c.index) {
        depth++;
        pos = o.index + 1;
      } else {
        depth--;
        pos = c.index + 1;
        if (depth === 0) { end = c.index + c[0].length; break; }
      }
    }
    blocks.push({ tag, html: html.slice(start, end) });
    cursor = end;
  }
  return blocks;
}

function listHeight(html: string, width: number): number {
  const items = html.match(/<li[\s>][\s\S]*?<\/li\s*>/gi) || [];
  return items.reduce((h, item) => h + textHeight(stripTags(item), width) + LI_MB, 0);
}

function tableHeight(html: string): number {
  const rows = (html.match(/<tr[\s>][\s\S]*?<\/tr\s*>/gi) || []).map(row =>
    (row.match(/<t[dh][\s>][\s\S]*?<\/t[dh]\s*>/gi) || []).map(cell => ({
      text: stripTags(cell),
      block: new RegExp('<' + BLOCK_TAGS + '[\\s>]', 'i').test(cell.replace(/^<t[dh][^>]*>/i, '')),
    })));
  if (rows.length === 0) return 0;

  // `table-layout: auto` reparte el ancho según el contenido; se aproxima con la
  // celda más larga de cada columna.
  const cols = Math.max(1, ...rows.map(r => r.length));
  const widest = new Array(cols).fill(1);
  for (const row of rows) {
    row.forEach((cell, i) => { widest[i] = Math.max(widest[i], cell.text.length); });
  }
  const total = widest.reduce((a, b) => a + b, 0);
  const colWidths = widest.map(len => Math.max(40, (len / total) * CONTENT_W));

  return rows.reduce((h, row) => {
    let rowH = 0;
    row.forEach((cell, i) => {
      const inner = Math.max(24, (colWidths[i] ?? CONTENT_W / cols) - 20); // menos padding lateral
      rowH = Math.max(rowH, textHeight(cell.text, inner, TABLE_FS) + (cell.block ? CELL_BLOCK_M : 0));
    });
    return h + rowH + CELL_CHROME;
  }, 0);
}

/** Contenido sin etiquetas de bloque (markdown suelto): una línea = un párrafo. */
function plainTextHeight(html: string): number {
  const lines = html.split('\n').map(l => stripTags(l)).filter(Boolean);
  if (lines.length === 0) return 0;
  return lines.reduce((h, line) => h + textHeight(line, CONTENT_W) + P_MARGIN, 0);
}

/**
 * Alto estimado de una sección, incluyendo el hueco hasta el título siguiente.
 */
export function estimateSectionHeight(html: string): number {
  const blocks = splitTopLevel(html);
  if (blocks.length === 0) return Math.round(plainTextHeight(html) + HEADING_MT);

  let height = 0;
  let prevMarginBottom = 0;
  let first = true;
  const add = (h: number, marginTop: number, marginBottom: number) => {
    if (!first) height += Math.max(prevMarginBottom, marginTop); // los márgenes colapsan
    height += h;
    prevMarginBottom = marginBottom;
    first = false;
  };

  for (const block of blocks) {
    if (/^h[1-6]$/.test(block.tag)) {
      add(HEADING_H, HEADING_MT, HEADING_MB);
    } else if (block.tag === 'ul' || block.tag === 'ol') {
      add(listHeight(block.html, CONTENT_W - LIST_PAD), LIST_MARGIN, LIST_MARGIN);
    } else if (block.tag === 'table') {
      add(tableHeight(block.html), TABLE_MARGIN, TABLE_MARGIN);
    } else {
      add(textHeight(stripTags(block.html), CONTENT_W), P_MARGIN, P_MARGIN);
    }
  }

  return Math.round(height + Math.max(prevMarginBottom, HEADING_MT));
}

/**
 * Corta por h3 y por h4: si solo mirase h4, un contenido titulado con h3 no
 * tiene ningún punto de corte, entra entero en una hoja y desborda pintándose
 * encima de sí mismo (silencioso, no falla nada).
 */
export function splitByH4(html: string): ContentSection[] {
  if (!html) return [];
  const parts = html.split(/(?=<h[34][\s>])/i);
  const sections: ContentSection[] = [];
  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;
    const headingMatch = trimmed.match(/<h[34][^>]*>(.*?)<\/h[34]>/i);
    sections.push({
      heading: headingMatch ? headingMatch[1].replace(/<[^>]+>/g, '').trim() : '',
      html: trimmed,
    });
  }
  return sections;
}

/**
 * Reparte las secciones en hojas sin reordenarlas: se van acumulando mientras
 * entren en el hueco de la hoja. Una sección que por sí sola no entra se queda
 * igual en su hoja y desborda: preferimos que se vea a que se pierda texto de
 * una cotización (el contenido habría que partirlo a mano en dos títulos).
 */
export function groupIntoPages(sections: ContentSection[]): ContentSection[][] {
  if (sections.length === 0) return [[]];
  const pages: ContentSection[][] = [];
  let current: ContentSection[] = [];
  let used = 0;

  for (const section of sections) {
    const height = estimateSectionHeight(section.html);
    const budget = pages.length === 0 ? FIRST_PAGE_BUDGET : CONT_PAGE_BUDGET;
    if (current.length > 0 && used + height > budget) {
      pages.push(current);
      current = [];
      used = 0;
    }
    current.push(section);
    used += height;
  }

  if (current.length > 0) pages.push(current);
  return pages;
}
